import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { IProductAccessories } from '../models/product-accessories.model';
import { User } from '../../shared/models/user';
import { IDownloadData } from '../models/article.model';
import { DataService } from '../../shared/services/data.service';
import { ConfigurationService } from '../../shared/services/configuration.service';
import { IEnclosure } from '../../shared/models/enclosure.model';
import { EnclosureTypes } from '../models/enclosure-type.model';
import { UtilService } from '../../shared/services/util.service';
import { IApartment } from '../models/apartment.model';
import { FileDownload } from '../../shared/services/file-download';
import { IProject } from '../../shared/models/project.model';
import { generateSelectedProductsObject } from '../../prisma-xs/helpers/bom-mapper.helper';
import { ToastService } from '../../toast/toast.service';
import { IBomModel } from '../../prisma-xs/models/bom.model';
import { mapToUpdateBomAccessoryFormat } from '../helpers/accessories.helper';

@Injectable({
  providedIn: 'root'
})
export class BomService extends FileDownload {

  private accessoriesCount$ = new BehaviorSubject<IProductAccessories[]>(null);

  constructor(
    private readonly dataService: DataService,
    private readonly utilService: UtilService,
    private readonly configurationService: ConfigurationService,
    private readonly toastService: ToastService
  ) {
    super(dataService, utilService, toastService);
  }

  getModelsInCSV(enclosure: IEnclosure, selectedItems: number[], selectedItemsAccessories: number[], apartments: number[], title: string) {
    const url = this.configurationService.getUrl('csv', 'project', enclosure.projectId, 'enclosure', enclosure.enclosureId);
    const res = this.dataService.downloadCSV(url, {
      productIds: selectedItems,
      accessoriesIds: selectedItemsAccessories,
      apartmentIds: apartments
    });
    return res.toPromise().then(d => {
      this.downloadFile(d, 'blob', this.generateFileName(enclosure, 'csv', title));
    });
  }

  getModelsInProjectCSV(project: IProject,
    selectedItems: number[],
    selectedItemsAccessories: number[],
    apartments: number[],
    title: string,
    selectedProjectComponents: number[],
    selectedEnclosures: number[]) {
      const url = this.configurationService.getUrl('csv', 'project', project.projectId);
      const res = this.dataService.downloadCSV(url, {
        productsDTO: generateSelectedProductsObject(selectedItems, selectedItemsAccessories, apartments, selectedProjectComponents),
        selectedEnclosures
      });

      return res.toPromise().then(d => {
        this.downloadFile(d, 'blob', this.generateProjectFileName(project, 'csv', title));
      });
  }

  getProductsWithLimit() {
    let baseUrl = this.configurationService.getUrl('ProductSelector', 'products');
    baseUrl += `?limit=10`;
    return this.dataService.get(baseUrl);
  }

  getAdditionalComponentsCatalogue(enclosureProductRangeId: number) {
    let baseUrl = this.configurationService.getUrl('ProductSelector', 'products', 'additional-components-catalogue');
    baseUrl += `?enclosureProductRangeId=${enclosureProductRangeId}`;

    return this.dataService.get(baseUrl);
  }

  getAdditionalComponentsProjectCatalogue() {
    return this.dataService.get(this.configurationService.getUrl('ProductSelector', 'products', 'additional-components-project-catalogue'));
  }

  getProductsWithFilter(keyword, searchString) {
    let baseUrl = this.configurationService.getUrl('ProductSelector', 'products');
    baseUrl += `/${keyword}?limit=10&${keyword}=${searchString}`;
    return this.dataService.get(baseUrl);
  }

  createAccessoriesProduct(productAccessories: IProductAccessories, projectId: number, enclosureId: number) {
    const url = this.configurationService.getUrl('Bom', 'accessories', 'project', projectId, 'enclosure', enclosureId, 'create');
    return this.dataService.post(url, productAccessories);
  }

  createProjectComponent(productAccessories: IProductAccessories, projectId: number) {
    const url = this.configurationService.getUrl('Bom', 'components', 'project', projectId, 'create');
    return this.dataService.post(url, productAccessories);
  }

  updateAccessoriesProduct(data, id, quantity, projectId: number, enclosureId: number) {
    // eslint-disable-next-line max-len
    const url = this.configurationService.getUrl('Bom', 'accessories', 'project', projectId, 'enclosure', enclosureId, 'update', id, 'quantity', quantity);
    return this.dataService.putWithId(url, data);
  }

  updateMultipleAccessories(items: IBomModel[], projectId: number, enclosureId: number) {
    const url = this.configurationService.getUrl('Bom', 'accessories', 'project', projectId, 'enclosure', enclosureId, 'update-multiple');
    return this.dataService.putWithId(url, mapToUpdateBomAccessoryFormat(items));
  }

  updateProjectComponent(data, id, quantity, projectId: number) {
    // eslint-disable-next-line max-len
    const url = this.configurationService.getUrl('Bom', 'components', 'project', projectId, 'update', id, 'quantity', quantity);
    return this.dataService.putWithId(url, data);
  }

  updateMultipleProjectComponents(items: IBomModel[], projectId: number) {
    // eslint-disable-next-line max-len
    const url = this.configurationService.getUrl('Bom', 'components', 'project', projectId, 'update-multiple');
    return this.dataService.putWithId(url, mapToUpdateBomAccessoryFormat(items))};

  updateAccessoriesProductDeliveryMethod(data, id, deliverToPanelBuilder, deliverToCustomer, projectId: number, enclosureId: number) {
    // eslint-disable-next-line max-len
    let url = this.configurationService.getUrl('Bom', 'accessories', 'project', projectId, 'enclosure', enclosureId, 'updatedeliverymethod', id);
    url += `?deliverToPanelBuilder=${deliverToPanelBuilder}&deliverToCustomer=${deliverToCustomer}`;
    return this.dataService.putWithId(url, data);
  }

  updateProjectComponentDeliveryMethod(data, id, deliverToPanelBuilder, deliverToCustomer, projectId: number) {
    // eslint-disable-next-line max-len
    let url = this.configurationService.getUrl('Bom', 'components', 'project', projectId, 'updatedeliverymethod', id);
    url += `?deliverToPanelBuilder=${deliverToPanelBuilder}&deliverToCustomer=${deliverToCustomer}`;
    return this.dataService.putWithId(url, data);
  }

  deleteAccessoriesProduct(id: number, projectId: number, enclosureId: number, multiple = false) {
    const url = this.configurationService.getUrl('Bom', 'accessories', 'project', projectId, 'enclosure', enclosureId, 'delete', id);
    return this.dataService.delete(url + '?multiple=' + multiple);
  }

  deleteProjectComponentProduct(id: number, projectId: number, multiple = false) {
    const url = this.configurationService.getUrl('Bom', 'components', 'project', projectId, 'delete', id);
    return this.dataService.delete(url + '?multiple=' + multiple);
  }

  getAccessories(projectId: number, enclosureId: number): Observable<IProductAccessories[]> {
    const url = this.configurationService.getUrl('Bom', 'accessories', 'project', projectId, 'enclosure', enclosureId);
    return this.dataService.get(url).pipe(
      map(
        (accs: IProductAccessories[]) => {
          this.emitAccessoriesCount(accs);
          return accs;
        }
      )
    );
  }

  getAccessoriesCountAsValue() {
    return this.accessoriesCount$.getValue();
  }

  emitAccessoriesCount(accessories: IProductAccessories[]) {
    this.accessoriesCount$.next(accessories);
  }

  getAccessoriesCount() {
    return this.accessoriesCount$.asObservable();
  }

  resetAccessoriesCount() {
    this.accessoriesCount$.next([]);
  }

  // eslint-disable-next-line max-len
  downloadBomFilePDF(e: IEnclosure, projectId: number, enclosureId: number, displayPrice: boolean, data: IDownloadData, fileName: string) {

    data.title = this.generateFileNameWithoutExtension(e, fileName);
    const url = this.configurationService.getUrl('PDF', 'bom', 'project', projectId, 'enclosure', e.enclosureId, 'showPrice', displayPrice);
    const name = this.generateFileName(e, 'pdf', fileName);

    return this.downloadFileBlob(url, data, name, enclosureId);
  }

  postToShoppingCart(bom: object[], projectId: number, hookUrl: string) {
    const url = this.configurationService.getUrl('Wsi', 'post', projectId);

    return this.dataService.post(url, {
        bom,
        url: hookUrl
      }
    );
  }

  postToEnclosureShoppingCart(bom: object[], projectId: number, hookUrl: string, enclosureId: number) {
    const url = this.configurationService.getUrl('Wsi', 'post', projectId, 'enclosure', enclosureId);

    return this.dataService.post(url, {
        bom,
        url: hookUrl
      }
    );
  }

  postToElbridge2ShoppingCart(bom: object[], projectId: number) {
    const url = this.configurationService.getUrl('Wsi', 'post_elbridge_2', projectId);

    return this.dataService.post(url, {
        bom
      }
    );
  }

  postToEnclosureElbridge2ShoppingCart(bom: object[], projectId: number, enclosureId: number) {
    const url = this.configurationService.getUrl('Wsi', 'post_elbridge_2', projectId, 'enclosure', enclosureId);

    return this.dataService.post(url, {
        bom
      }
    );
  }

  reportCopyButtonCallOnProject(projectId: number) {
    const url = this.configurationService.getUrl('Wsi', 'report_copy', projectId);

    return this.dataService.post(url, {});
  }

  reportCopyButtonCallOnEnclosure(projectId: number, enclosureId: number) {
    const url = this.configurationService.getUrl('Wsi', 'report_copy', projectId, 'enclosure', enclosureId);

    return this.dataService.post(url, {});
  }

  downloadProjectBomFilePDF(project: IProject, displayPrice: boolean, data: IDownloadData, fileName: string) {
    data.title = this.generateProjectFileNameWithoutExtension(project, fileName);
    const url = this.configurationService.getUrl('PDF', 'bom', 'project', project.projectId);
    const name = this.generateProjectFileName(project, 'pdf', fileName);

    return this.downloadFileBlob(url, data, name);
  }

  downloadDispositionLayoutPDF(enclosure, projectId: number, enclosureId: number, fileName: string, showCovers, selectedApartments?: number[]) {

    const title = this.generateFileNameWithoutExtension(enclosure, fileName);
    const url = this.configurationService.getUrl('PDF', 'swb', 'project', projectId, 'enclosure', enclosure?.enclosureId, 'showCovers', showCovers);
    const name = this.generateFileName(enclosure, 'pdf', fileName);
    const data = selectedApartments ? { title, selectedApartments } : { title };

    return this.downloadFileBlob(url, data, name, enclosureId);
  }

  // eslint-disable-next-line max-len
  downloadEnclosureLayoutPDF(enclosure, projectId: number, enclosureId: number, enclosureType: EnclosureTypes, fileName, showCovers, selectedApartments?: number[]) {

    const title = this.generateFileNameWithoutExtension(enclosure, fileName);
    const name = this.generateFileName(enclosure, 'pdf', fileName);

    let url = '';
    if (enclosureType === EnclosureTypes.PRISMA_XS || enclosureType === EnclosureTypes.PRAGMA) {
      // eslint-disable-next-line max-len
      url = this.configurationService.getUrl('PDF', 'configuration', 'project', projectId, 'enclosure', enclosure?.enclosureId, 'showCovers', showCovers);
    } else {
      url = this.configurationService.getUrl('PDF', 'configuration', 'project', projectId, 'enclosure', enclosure?.enclosureId);
    }

    const data = selectedApartments ? { title, selectedApartments } : { title };

    return this.downloadFileBlob(url, data, name, enclosureId);
  }

  // eslint-disable-next-line max-len
  downloadLabelsPDF(e: IEnclosure, projectId: number, enclosureId: number, enclosureType: EnclosureTypes, data: IDownloadData, fileName: string, isFlipped: boolean) {

    const url = this.configurationService.getUrl('PDF', 'labels', 'project', projectId, 'enclosure', e?.enclosureId, 'isFlipped', isFlipped);
    const name = this.generateFileName(e, 'pdf', fileName);

    return this.downloadFileBlob(url, data, name, enclosureId);
  }

  downloadBomFileExcel(enclosure, projectId: number, enclosureId: number, displayPrice: boolean, data, fileName: string) {

    const url = this.configurationService.getUrl('Excel', 'bom', 'project', projectId, 'enclosure', enclosureId, 'showPrice', displayPrice);
    const name = this.generateFileName(enclosure, 'xlsx', fileName);

    return this.downloadFileBlob(url, data, name, enclosureId);
  }

  downloadProjectBomFileExcel(project: IProject, displayPrice: boolean, data, fileName: string) {
    const url = this.configurationService.getUrl('Excel', 'bom', 'project', project.projectId, 'showPrice', displayPrice);
    const name = this.generateProjectFileName(project, 'xlsx', fileName);

    return this.downloadFileBlob(url, data, name);
  }

  // eslint-disable-next-line max-len
  generateDataForDownload(userInfo: User, showPrice: boolean, showAll: boolean, selectedItems?, selectedAccessories?, selectedApartments?, selectedEncs?, selectedComponentsIds?) {
    const data: IDownloadData = {
      userInfosAuth0: {
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
        phone: userInfo?.phone,
        email: userInfo.email
      },
      options: {
        showPrice,
        showAll
      },
      selectedEnclosures: selectedEncs ?? undefined
    };

    // eslint-disable-next-line max-len
    data.selectedProducts = generateSelectedProductsObject(selectedItems, selectedAccessories, selectedApartments, selectedComponentsIds);

    return data;
  }

  // eslint-disable-next-line max-len
  generateDataForDownloadWithSelectedApartments(userInfo: User, showPrice: boolean, showAll: boolean, selectedApartments: number[], selectedEncs?): IDownloadData {
    return {
      userInfosAuth0: {
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
        phone: userInfo?.phone,
        email: userInfo.email
      },
      options: {
        showPrice,
        showAll
      },
      selectedProducts: {
        apartmentIds: selectedApartments
      },
      selectedEnclosures: selectedEncs ?? undefined
    };
  }

  generateDataForDocuments(userInfo: User, selectedApartments: number[], selectedEncs?: number[]): IDownloadData {
    return {
      userInfosAuth0: {
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
        phone: userInfo?.phone,
        email: userInfo.email
      },
      options: {
        showAll: true
      },
      selectedProducts: {
        apartmentIds: selectedApartments
      },
      selectedEnclosures: selectedEncs ?? null
    };
  }

  // eslint-disable-next-line max-len
  downloadLegendsPDF(enclosure: IEnclosure, projectId: number, enclosureId: number, apartment: IApartment, fileName: string) {

    const title = this.generateFileNameWithoutExtension(enclosure, fileName + ' ' + apartment.apartmentName);
    const name = this.generateFileName(enclosure, 'pdf', fileName + ' ' + apartment.apartmentName);

    const url = this.configurationService.getUrl('PDF', 'legends', 'project', projectId, 'enclosure', enclosure?.enclosureId,
      'apartment', apartment.apartmentId);

    return this.downloadFileBlob(url, { title }, name, enclosureId);
  }

  downloadLegendsWord(enclosure: IEnclosure, projectId: number, enclosureId: number, apartment: IApartment, fileName: string) {
    const title = this.generateFileNameWithoutExtension(enclosure, fileName + ' ' + apartment.apartmentName);
    const name = this.generateFileName(enclosure, 'docx', fileName + ' ' + apartment.apartmentName);

    const url = this.configurationService.getUrl('Word', 'legends', 'project', projectId, 'enclosure', enclosure?.enclosureId,
      'apartment', apartment.apartmentId);

    return this.downloadFileBlob(url, { title }, name, enclosureId);
  }

  // eslint-disable-next-line max-len
  downloadRoutineVerificationPDF(enclosure: IEnclosure, projectId: number, enclosureId: number, apartment: IApartment, fileName: string) {
    const title = this.generateFileNameWithoutExtension(enclosure, fileName + ' ' + apartment.apartmentName);
    const name = this.generateFileName(enclosure, 'pdf', fileName + ' ' + apartment.apartmentName);

    const url = this.configurationService.getUrl('PDF', 'protocols', 'project', projectId, 'enclosure', enclosure?.enclosureId,
      'apartment', apartment.apartmentId);

    return this.downloadFileBlob(url, { title }, name, enclosureId);
  }

  // eslint-disable-next-line max-len
  downloadRoutineVerificationWord(enclosure: IEnclosure, projectId: number, enclosureId: number, apartment: IApartment, fileName: string) {
    const title = this.generateFileNameWithoutExtension(enclosure, fileName + ' ' + apartment.apartmentName);
    const name = this.generateFileName(enclosure, 'docx', fileName + ' ' + apartment.apartmentName);

    const url = this.configurationService.getUrl('Word', 'protocols', 'project', projectId, 'enclosure', enclosure?.enclosureId,
      'apartment', apartment.apartmentId);

    return this.downloadFileBlob(url, { title }, name, enclosureId);
  }

  downloadRatingPlateWord(enclosure, projectId: number, enclosureId: number, fileName: string, bomData: IDownloadData) {
    const title = this.generateFileNameWithoutExtension(enclosure, fileName);

    const url = this.configurationService.getUrl('Word', 'rating_plate', 'project', projectId, 'enclosure', enclosure?.enclosureId);
    const name = this.generateFileName(enclosure, 'docx', fileName);

    return this.downloadFileBlob(url, { ...bomData, title }, name, enclosureId);
  }

  downloadRatingPlateExcel(enclosure, projectId: number, enclosureId: number, fileName: string, bomData: IDownloadData) {
    const title = this.generateFileNameWithoutExtension(enclosure, fileName);

    const url = this.configurationService.getUrl('Excel', 'ratingPlate', 'project', projectId, 'enclosure', enclosure?.enclosureId);
    const name = this.generateFileName(enclosure, 'xlsx', fileName);

    return this.downloadFileBlob(url, { ...bomData, title }, name, enclosureId);
  }

  getProjectComponents(projectId: number) {
    if(!projectId) {
      return;
    }
    const url = this.configurationService.getUrl('BOM', 'components', 'project', projectId);
    return this.dataService.get(url);
  }

  getProjectAccessories(projectId: number) {
    if(!projectId) {
      return;
    }
    const url = this.configurationService.getUrl('BOM', 'accessories', 'project', projectId);
    return this.dataService.get(url);
  }
}
